/**
 * Specialty Registry — type definitions.
 *
 * rev v11.0.0 · Phase 2 / C1
 *
 * WHAT THIS PACKAGE IS FOR
 * ------------------------
 * The platform must present a different user experience per medical
 * specialty. Doing that by editing 89 routes per specialty does not scale
 * and cannot be rolled back.
 *
 * Instead, every specialty is described by ONE declarative manifest: its
 * theme tokens, its navigation, its clinical panels, its NGAP scope, its
 * terminology subset and its device profiles. The shell reads the manifest;
 * it does not know the name of any specialty.
 *
 * The result: changing the experience of a specialty becomes a data change,
 * and rolling it back becomes a feature-flag change.
 */
import { z } from "zod";

/**
 * Theme tokens applied to the `[data-specialty="<slug>"]` scope.
 *
 * These are the SAME variables Tailwind v4 already exposes through
 * `@theme inline` in apps/web/src/styles.css. The 48 shadcn/ui components
 * read them, so overriding the tokens recolours the interface without
 * forking a single component.
 */
export const ThemeTokensSchema = z.object({
  /** oklch() colour string. */
  primary: z.string().min(1),
  primaryForeground: z.string().min(1),
  accent: z.string().min(1),
  accentForeground: z.string().min(1),
  destructive: z.string().min(1),
  success: z.string().min(1),
  warning: z.string().min(1),
  surface: z.string().min(1),
  /** CSS length. */
  radius: z.string().min(1),
  /** CSS font stack. */
  fontDisplay: z.string().min(1),
});
export type ThemeTokens = z.infer<typeof ThemeTokensSchema>;

/**
 * A navigation module visible to a specialty.
 *
 * `requires` lists capability flags: a module is hidden when the tenant or
 * the physician lacks the capability. This is what lets cardiology show the
 * ECG module while dermatology hides it, without conditional code in the
 * navigation component.
 */
export const NavModuleSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** lucide-react icon name. */
  icon: z.string().min(1),
  /** Absolute route under /clinical. */
  route: z.string().min(1),
  order: z.number().int().nonnegative(),
  requires: z.array(z.string()).default([]),
});
export type NavModule = z.infer<typeof NavModuleSchema>;

/** A clinical panel rendered on the specialty workspace. */
export const PanelSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  icon: z.string().min(1),
  /** Component key resolved by the web layer. */
  component: z.string().min(1),
  order: z.number().int().nonnegative().default(0),
  requires: z.array(z.string()).default([]),
});
export type PanelSchemaType = z.infer<typeof PanelSchema>;

/** NGAP association: which part of the nomenclature this specialty uses. */
export const NgapScopeSchema = z.object({
  /** Value of `ngap.actes.sub_specialty` / the catalogue specialty scope. */
  specialtyScope: z.string().min(1),
  subSpecialties: z.array(z.string()).default([]),
  /**
   * Number of acts of this specialty in the NGAP Maroc catalogue
   * (docs/billing/ngap-classified.json).
   *
   * Zero means the specialty has no catalogue act of its own: its acts are
   * billed inside another specialty scope. The count is asserted by
   * `scripts/check-specialty-drift.mjs`, which fails CI when the catalogue
   * and the registry disagree.
   */
  catalogueActs: z.number().int().nonnegative(),
});
export type NgapScope = z.infer<typeof NgapScopeSchema>;

/** Terminology subset pre-loaded for this specialty. */
export const TerminologyScopeSchema = z.object({
  icd10: z.array(z.string()).default([]),
  loinc: z.array(z.string()).default([]),
  /** Extra SNOMED-ish concept identifiers used by the terminology server. */
  snomed: z.array(z.string()).default([]),
});
export type TerminologyScope = z.infer<typeof TerminologyScopeSchema>;

/** The full manifest of one specialty. */
export const SpecialtyDefinitionSchema = z.object({
  /** Canonical slug. Stable, kebab-case, never renamed. */
  id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  /** Display label (French, as used by the Moroccan clinical community). */
  label: z.string().min(1),
  /** Compact label for tab headers and dense layouts. */
  short: z.string().min(1),
  /** Clinical scope statement. */
  scope: z.string().min(1),
  /**
   * Legacy specialty codes that resolve to this slug.
   *
   * This is the vocabulary bridge. The platform used two incompatible
   * vocabularies: kebab-case slugs in the UI and UPPERCASE codes in the
   * NGAP catalogue. `MED` and `MEDG` both mean general medicine; `OBST`
   * is obstetrics inside gynaecology. The alias list makes every one of
   * those resolutions explicit and testable.
   *
   * The legacy vocabulary itself is preserved in
   * reference-data/ngap/legacy-specialty-codes.json; the CI gate
   * (scripts/check-specialty-drift.mjs) asserts that every alias below
   * exists there and that nothing there is missing.
   */
  aliases: z.array(z.string()).default([]),
  /** Physician specialisations allowed to work in this specialty. */
  doctorSpecs: z.array(z.string()).default([]),
  /** Feature-flag key. One flag per specialty — never a global flag. */
  featureFlag: z.string().min(1),
  theme: ThemeTokensSchema,
  navigation: z.array(NavModuleSchema).default([]),
  panels: z.array(PanelSchema).default([]),
  ngap: NgapScopeSchema,
  terminology: TerminologyScopeSchema.default({ icd10: [], loinc: [], snomed: [] }),
  /** Device profile identifiers (from the 98 supported profiles). */
  deviceProfiles: z.array(z.string()).default([]),
  /** Locale codes this specialty ships translations for. */
  locales: z.array(z.string()).default(["fr", "ar"]),
});
export type SpecialtyDefinition = z.infer<typeof SpecialtyDefinitionSchema>;

/**
 * Compile-time helper that validates a manifest at build time.
 *
 * A malformed manifest must fail the build, not the browser.
 */
export function defineSpecialty(input: SpecialtyDefinition): SpecialtyDefinition {
  const parsed = SpecialtyDefinitionSchema.safeParse(input);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `${i.path.join(".")}: ${i.message}`)
      .join("; ");
    throw new Error(`invalid specialty manifest '${input.id}': ${issues}`);
  }
  return parsed.data;
}
